export const SITE_LOCALES = ['en', 'ja', 'ko', 'es'] as const;
export const SEO_PAGE_FAMILIES = ['ai-design-tools'] as const;
export const SANITY_PAGE_FAMILIES = [
  'tool',
  'solution',
  'campaign',
  'industry',
  'occasion',
  'workflow',
  'input',
  'role',
  'template',
  'guide',
] as const;
export const SANITY_SEO_DOCUMENT_TYPES = ['aiTool'] as const;

export type SiteLocale = (typeof SITE_LOCALES)[number];
export type SeoPageFamily = (typeof SEO_PAGE_FAMILIES)[number];
export type SanityPageFamily = (typeof SANITY_PAGE_FAMILIES)[number];
export type SanitySeoDocumentType = (typeof SANITY_SEO_DOCUMENT_TYPES)[number];
export type SeoContentSection = SeoPageFamily | 'spotlight' | 'blog';

export interface TranslationAlternate {
  language: SiteLocale;
  slug: string;
}

const LOCALE_PREFIXES = new Set<SiteLocale>(['ja', 'ko', 'es']);

const SANITY_PAGE_FAMILIES_BY_SEO: Record<SeoPageFamily, readonly SanityPageFamily[]> = {
  'ai-design-tools': [
    'tool',
    'campaign',
    'industry',
    'occasion',
    'workflow',
    'input',
    'role',
    'template',
    'guide',
    'solution',
  ],
};

const SEO_PAGE_FAMILY_BY_SANITY: Partial<Record<SanityPageFamily, SeoPageFamily>> = {
  tool: 'ai-design-tools',
  campaign: 'ai-design-tools',
  industry: 'ai-design-tools',
  occasion: 'ai-design-tools',
  workflow: 'ai-design-tools',
  input: 'ai-design-tools',
  role: 'ai-design-tools',
  template: 'ai-design-tools',
  guide: 'ai-design-tools',
  solution: 'ai-design-tools',
};

const SANITY_DOCUMENT_TYPE_BY_SEO: Record<SeoPageFamily, SanitySeoDocumentType> =
  Object.fromEntries(SEO_PAGE_FAMILIES.map((family) => [family, 'aiTool'])) as Record<
    SeoPageFamily,
    SanitySeoDocumentType
  >;

const HREFLANG_BY_LOCALE: Record<SiteLocale, string> = {
  en: 'en-US',
  ja: 'ja-JP',
  ko: 'ko-KR',
  es: 'es-ES',
};

const OG_LOCALE_BY_LOCALE: Record<SiteLocale, string> = {
  en: 'en_US',
  ja: 'ja_JP',
  ko: 'ko_KR',
  es: 'es_ES',
};

export function normalizeSiteLocale(raw: unknown): SiteLocale | null {
  if (typeof raw !== 'string') return null;
  const locale = raw.toLowerCase().trim().split('-')[0];
  return SITE_LOCALES.includes(locale as SiteLocale) ? (locale as SiteLocale) : null;
}

export function normalizeSeoPageFamily(raw: unknown): SeoPageFamily | null {
  if (typeof raw !== 'string') return null;
  const family = raw.toLowerCase().trim();
  return SEO_PAGE_FAMILIES.includes(family as SeoPageFamily) ? (family as SeoPageFamily) : null;
}

export function normalizeSanityPageFamily(raw: unknown): SanityPageFamily | null {
  if (typeof raw !== 'string') return null;
  const family = raw.toLowerCase().trim();
  return SANITY_PAGE_FAMILIES.includes(family as SanityPageFamily)
    ? (family as SanityPageFamily)
    : null;
}

export function sanityPageFamiliesForSeo(family: SeoPageFamily): readonly SanityPageFamily[] {
  return SANITY_PAGE_FAMILIES_BY_SEO[family];
}

export function sanityDocumentTypeForSeo(family: SeoPageFamily): SanitySeoDocumentType {
  return SANITY_DOCUMENT_TYPE_BY_SEO[family];
}

export function seoPageFamilyFromDocument(
  documentType: unknown,
  legacyPageFamily?: unknown
): SeoPageFamily | null {
  if (documentType !== 'aiTool') return null;
  return seoPageFamilyFromSanity(legacyPageFamily);
}

export function seoPageFamilyFromSanity(raw: unknown): SeoPageFamily | null {
  const family = normalizeSanityPageFamily(raw);
  return family ? (SEO_PAGE_FAMILY_BY_SANITY[family] ?? null) : null;
}

export function seoPageFamilyFromPath(pathname: string): SeoPageFamily | null {
  const path = stripLocalePrefix(pathname).split(/[?#]/, 1)[0] || '/';
  const firstSegment = path.split('/').filter(Boolean)[0];
  return normalizeSeoPageFamily(firstSegment);
}

export function siteLocaleFromRouteParam(raw: unknown): SiteLocale {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return normalizeSiteLocale(value) ?? 'en';
}

export function siteLocaleFromPath(pathname: string): SiteLocale | null {
  const firstSegment = pathname.split(/[?#]/, 1)[0]?.split('/').filter(Boolean)[0];
  const locale = normalizeSiteLocale(firstSegment);
  return locale && LOCALE_PREFIXES.has(locale) ? locale : null;
}

export function stripLocalePrefix(path: string): string {
  const match = path.match(/^\/(ja|ko|es)(?=\/|$)/i);
  if (!match) return path || '/';
  const stripped = path.slice(match[0].length);
  return stripped.startsWith('/') ? stripped : stripped ? `/${stripped}` : '/';
}

export function localizedPath(locale: SiteLocale, unprefixedPath: string): string {
  const normalizedPath = ensureLeadingSlash(stripLocalePrefix(unprefixedPath));
  if (locale === 'en') return normalizedPath;
  return normalizedPath === '/' ? `/${locale}` : `/${locale}${normalizedPath}`;
}

export function absoluteLocalizedUrl(
  locale: SiteLocale,
  unprefixedPath: string,
  origin = 'https://veeso.ai'
): string {
  return `${origin.replace(/\/+$/, '')}${localizedPath(locale, unprefixedPath)}`;
}

export function hreflangFor(locale: SiteLocale): string {
  return HREFLANG_BY_LOCALE[locale];
}

export function ogLocaleFor(locale: SiteLocale): string {
  return OG_LOCALE_BY_LOCALE[locale];
}

export function normalizeTranslationAlternates(
  raw: unknown,
  current?: TranslationAlternate
): TranslationAlternate[] {
  const byLocale = new Map<SiteLocale, TranslationAlternate>();
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const candidate = item as { language?: unknown; slug?: unknown };
      const language = normalizeSiteLocale(candidate.language);
      const slug = typeof candidate.slug === 'string' ? candidate.slug.trim() : '';
      if (language && slug) byLocale.set(language, { language, slug });
    }
  }
  if (current?.slug) byLocale.set(current.language, current);
  return SITE_LOCALES.flatMap((language) => {
    const alternate = byLocale.get(language);
    return alternate ? [alternate] : [];
  });
}

export function buildDetailHreflangLinks(
  section: SeoContentSection,
  alternates: TranslationAlternate[],
  origin = 'https://veeso.ai'
) {
  const links = alternates.map((alternate) => ({
    rel: 'alternate',
    hreflang: hreflangFor(alternate.language),
    href: absoluteLocalizedUrl(
      alternate.language,
      `/${section}/${encodeURIComponent(alternate.slug)}`,
      origin
    ),
    key: `alternate-${alternate.language}`,
  }));
  const english = alternates.find((alternate) => alternate.language === 'en');
  if (english) {
    links.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: absoluteLocalizedUrl('en', `/${section}/${encodeURIComponent(english.slug)}`, origin),
      key: 'alternate-x-default',
    });
  }
  return links;
}

export function buildIndexHreflangLinks(
  section: SeoContentSection,
  origin = 'https://veeso.ai',
  availableLocales: readonly SiteLocale[] = SITE_LOCALES
) {
  return buildPathHreflangLinks(`/${section}`, origin, availableLocales);
}

export function buildPathHreflangLinks(
  unprefixedPath: string,
  origin = 'https://veeso.ai',
  availableLocales: readonly SiteLocale[] = SITE_LOCALES
) {
  const localeSet = new Set(availableLocales);
  const locales = SITE_LOCALES.filter((locale) => localeSet.has(locale));
  const links = locales.map((locale) => ({
    rel: 'alternate',
    hreflang: hreflangFor(locale),
    href: absoluteLocalizedUrl(locale, unprefixedPath, origin),
    key: `alternate-${locale}`,
  }));
  if (localeSet.has('en')) {
    links.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: absoluteLocalizedUrl('en', unprefixedPath, origin),
      key: 'alternate-x-default',
    });
  }
  return links;
}

export function buildDetailLanguagePaths(
  section: SeoContentSection,
  alternates: TranslationAlternate[]
): Readonly<Partial<Record<SiteLocale, string>>> {
  return Object.fromEntries(
    alternates.map((alternate) => [
      alternate.language,
      localizedPath(alternate.language, `/${section}/${encodeURIComponent(alternate.slug)}`),
    ])
  );
}

function ensureLeadingSlash(path: string): string {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}
